import React, { useState, useEffect, useCallback } from 'react';
import './VocabularyPractice.css';
import api from './api';
import Login from './Login';

// 调试日志函数
export const logDebug = (message, data = null) => {
  const debugInfo = `${new Date().toLocaleString()}: ${message}${data ? ` - ${JSON.stringify(data)}` : ''}`;
  console.log(debugInfo);
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('accessToken');
};

const VocabularyPractice = () => {
  // 状态管理
  const [debugInfo, setDebugInfo] = useState('');
  const [vocabList, setVocabList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredVocabList, setFilteredVocabList] = useState([]);
  // 后端同步的错误统计
  // 已移除未使用的testHistory状态
  const [vocabStats, setVocabStats] = useState([]);
  // 当前用户
  const [currentUser, setCurrentUser] = useState(null);
  const [isLogin, setIsLogin] = useState(false);
  // 复习参数设置
  const [reviewParams, setReviewParams] = useState({
    minErrorRate: 0, // 最小错误率，高于此值的单词需要优先复习（已修改为0，确保所有错误单词都显示）
    maxInterval: 30,   // 最大复习间隔（天）
    difficultyWeight: 0.5 // 难度系数权重
  });
  // 测试功能相关状态
  const [testMode, setTestMode] = useState(false);
  const [testSize, setTestSize] = useState(10);
  const [availableTestSizes, setAvailableTestSizes] = useState([10]);
  const [testVocabList, setTestVocabList] = useState([]);
  const [testResults, setTestResults] = useState({
    correct: 0,
    incorrect: 0,
    wrongAnswers: []
  });
  const [testCompleted, setTestCompleted] = useState(false);

  // 自定义日志函数
  const logDebug = useCallback((message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = data 
      ? `[${timestamp}] ${message}: ${JSON.stringify(data)}` 
      : `[${timestamp}] ${message}`;
    console.log(`[${timestamp}] ${message}`, data);
    setDebugInfo(prev => `${prev}\n${logMessage}`.slice(-10000));
    // 添加alert提示 (生产环境可注释)
    // alert(`[调试] ${message}`);
  }, []);

  // 手动加载分类函数
  const manuallyFetchCategories = async () => {
    logDebug('手动触发分类加载');
    setLoading(true);
    try {
      const response = await api.fetchWithAuth('/api/sentences/categories/?type=vocabulary', {
        method: 'GET'
      });
      logDebug('手动加载分类API响应状态', response.status);
      if (!response.ok) {
        throw new Error(`分类API请求失败，状态码: ${response.status}`);
      }
      const data = await response.json();
      const categories = Array.isArray(data) ? data : (data.results || []);
      setCategories(categories);
      logDebug('手动加载分类完成', { 数量: categories.length });
    } catch (err) {
      logDebug('手动加载分类失败', err.message);
      setError(`分类加载失败: ${err.message}`);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };



  // 从后端获取单词统计
  const fetchVocabStats = useCallback(async () => {
    logDebug('开始获取单词统计');
    try {
      const stats = await api.getVocabStats();
      setVocabStats(stats);
      logDebug('单词统计加载完成', { 总记录数: stats.length });
    } catch (err) {
      logDebug('单词统计加载失败', err.message);
    }
  }, [logDebug]);

  // 上报测试结果到后端
  const reportTestResult = useCallback(async (vocabId, errorCount) => {
    logDebug('上报测试结果', { vocabId, errorCount });

    if (!isAuthenticated()) {
      logDebug('未登录，跳过上报测试结果（游客模式）');
      return;
    }

    try {
      const data = await api.reportTestResult(vocabId, errorCount);
      logDebug('测试结果上报成功', data);
      // 刷新单词统计数据
      fetchVocabStats();
    } catch (err) {
      logDebug('测试结果上报失败', err.message);
    }
  }, [logDebug, fetchVocabStats]);

  // 检查登录状态
  const checkLoginStatus = useCallback(async () => {
    if (isAuthenticated()) {
      try {
        const userInfo = await api.getUserInfo();
        setCurrentUser(userInfo);
        setIsLogin(true);
        logDebug('用户已登录', userInfo);
      } catch (err) {
        logDebug('获取用户信息失败', err.message);
        api.logout();
        setIsLogin(true);
        setCurrentUser({ username: '游客' });
      }
    } else {
      setIsLogin(true);
      setCurrentUser({ username: '游客' });
    }
  }, [logDebug]);

  // 初始检查登录状态
  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  // 初始加载所有词汇
  useEffect(() => {
    if (!isLogin) return;

    const loadAllVocab = async () => {
      logDebug('开始获取所有词汇');
      try {
        const allVocab = await api.getVocabulary();
        setVocabList(allVocab);
        setFilteredVocabList(allVocab);
        logDebug('所有词汇加载完成', { 总数量: allVocab.length });
      } catch (err) {
        logDebug('初始词汇加载失败', err.message);
        setError(`加载失败: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadAllVocab();
  }, [logDebug, isLogin]);

  // 初始加载单词统计
  useEffect(() => {
    if (isLogin) {
      fetchVocabStats();
    }
  }, [isLogin, fetchVocabStats]);

  // 获取分类数据
  useEffect(() => {
    if (!isLogin) {
      logDebug('未登录，跳过获取分类数据');
      return;
    }

    const fetchCategories = async () => {
      logDebug('开始获取分类数据');
      setLoading(true); // 开始加载分类时设置loading为true
      try {
        // 确保URL以/开头
        logDebug('准备调用分类API');
        const response = await api.fetchWithAuth('/api/sentences/categories/?type=vocabulary', {
          method: 'GET'
        });
        logDebug('分类API响应状态', response.status);
        
        if (!response.ok) {
          throw new Error(`分类API请求失败，状态码: ${response.status}`);
        }
        
        logDebug('分类API响应成功，开始解析数据');
        const data = await response.json();
        logDebug('分类API返回数据', data);
        const categories = Array.isArray(data) ? data : (data.results || []);
        setCategories(categories);
        logDebug('分类数据加载完成', { 数量: categories.length });
        
        // 额外检查分类数据
        if (categories.length === 0) {
          logDebug('分类数据为空');
        } else {
          logDebug('分类数据示例', categories[0]);
        }
      } catch (err) {
        logDebug('分类加载失败', err.message);
        setError(`分类加载失败: ${err.message}`);
        setCategories([]); // 确保categories为空数组
      } finally {
        logDebug('分类加载完成（无论成功或失败）');
        setLoading(false); // 无论成功失败，最后都设置loading为false
      }
    };

    fetchCategories();
  }, [logDebug, isLogin]);

  // 分类筛选
  useEffect(() => {
    if (!isLogin || vocabList.length === 0) {
      logDebug('未登录或词汇列表为空，跳过筛选');
      return;
    }

    const filterVocab = async () => {
      logDebug('开始筛选分类', { 选中分类: selectedCategory });
      setLoading(true);
      try {
        let params = { page_size: 10000 };
        if (selectedCategory !== 'all') {
          params.categories__id = selectedCategory;
        }
        
        // 构建查询字符串
        const queryString = new URLSearchParams(params).toString();
        const filtered = await api.getVocabulary(queryString);
        logDebug('筛选完成', { 筛选后数量: filtered.length });
        
        setFilteredVocabList(filtered);
        setCurrentIndex(0); // 重置到第一条
        setFeedback('');
        setTestCompleted(false);
        setTestMode(false);

        // 设置可用的测试数量选项
        const sizes = [];
        if (filtered.length >= 10) sizes.push(10);
        if (filtered.length >= 20) sizes.push(20);
        if (filtered.length >= 30) sizes.push(30);
        if (filtered.length >= 40) sizes.push(40);
        if (filtered.length >= 50) sizes.push(50);
        if (sizes.length === 0 && filtered.length > 0) {
          sizes.push(filtered.length);
        }
        setAvailableTestSizes(sizes);
        // 默认选择最小的可用测试数量
        if (sizes.length > 0) {
          setTestSize(sizes[0]);
        }
      } catch (err) {
        logDebug('筛选失败', err.message);
        setFilteredVocabList(vocabList); // 失败时显示全部
      } finally {
        setLoading(false);
      }
    };

    filterVocab();
  }, [selectedCategory, vocabList, logDebug, isLogin]);

  // 登录成功后的处理
  const handleLogin = (userData) => {
    setCurrentUser(userData);
    setIsLogin(true);
    logDebug('登录成功', userData);
    // 登录成功后重新加载数据
    setLoading(true);
  };

  // 登出处理
  const handleLogout = () => {
    api.logout();
    setIsLogin(false);
    setCurrentUser(null);
    setVocabList([]);
    setFilteredVocabList([]);
    setVocabStats([]);
    logDebug('用户已登出');
  };

  // 检查答案并更新错误统计
  const checkAnswer = useCallback(() => {
    if (!filteredVocabList.length) return;
    
    let current;
    if (testMode) {
      current = testVocabList[currentIndex];
    } else {
      current = filteredVocabList[currentIndex];
    }

    const isMatch = userInput.trim().toLowerCase() === current.english.toLowerCase();
    setIsCorrect(isMatch);
    setFeedback(isMatch ? '正确！' : `错误，正确答案：${current.english}`);

    // 上报测试结果到后端
    const errorCount = isMatch ? 0 : 1;
    
    // 非测试模式下给出提示
    if (!testMode) {
      if (!window.confirm('您尚未开始测试，确定要上报此次练习结果吗？')) {
        return;
      }
    }
    
    reportTestResult(current.id, errorCount)
      .catch(error => {
        logDebug('上报测试结果异常', error.message);
        setError(`上报测试结果失败: ${error.message}`); // 显示错误提示
      });

    // 更新测试结果
    if (testMode) {
      setTestResults(prev => ({
        correct: prev.correct + (isMatch ? 1 : 0),
        incorrect: prev.incorrect + (isMatch ? 0 : 1),
        wrongAnswers: isMatch ? prev.wrongAnswers : [...prev.wrongAnswers, {
          chinese: current.chinese,
          correctEnglish: current.english,
          userAnswer: userInput
        }]
      }));

      // 检查是否完成测试
      if (currentIndex >= testVocabList.length - 1) {
        setTimeout(() => {
          setTestCompleted(true);
        }, 1000);
      }
    }
  }, [filteredVocabList, testMode, testVocabList, currentIndex, userInput, setIsCorrect, setFeedback, logDebug, reportTestResult, setError, setTestResults, setTestCompleted]);

  // 重置输入
  const resetInput = useCallback(() => {
    setUserInput('');
    setFeedback('');
    setIsCorrect(null);
  }, [setUserInput, setFeedback, setIsCorrect]);

  // 导航控制
  const nextVocab = useCallback(() => {
    if (testMode) {
      if (testVocabList.length && currentIndex < testVocabList.length - 1) {
        setCurrentIndex(prev => prev + 1);
        resetInput();
      }
    } else if (filteredVocabList.length) {
      setCurrentIndex(prev => (prev + 1) % filteredVocabList.length);
      resetInput();
    }
  }, [testMode, testVocabList, currentIndex, filteredVocabList, setCurrentIndex, resetInput]);

  const prevVocab = useCallback(() => {
    if (testMode) {
      if (testVocabList.length && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
        resetInput();
      }
    } else if (filteredVocabList.length) {
      setCurrentIndex(prev => (prev - 1 + filteredVocabList.length) % filteredVocabList.length);
      resetInput();
    }
  }, [testMode, testVocabList, currentIndex, filteredVocabList, setCurrentIndex, resetInput]);

  // 测试控制函数
  const startTest = () => {
    if (filteredVocabList.length === 0) return;

    // 从筛选后的词汇中随机选择指定数量的词汇
    const shuffled = [...filteredVocabList].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, testSize);

    setTestVocabList(selected);
    setCurrentIndex(0);
    setTestResults({
      correct: 0,
      incorrect: 0,
      wrongAnswers: []
    });
    setTestCompleted(false);
    setTestMode(true);
    resetInput();
    logDebug('开始测试', { 测试数量: testSize });
  };

  const resetTest = () => {
    setTestMode(false);
    setTestCompleted(false);
    setCurrentIndex(0);
    resetInput();
    logDebug('重置测试');
  };

  // 键盘事件
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Enter') checkAnswer();
      else if (e.key === 'ArrowRight') nextVocab();
      else if (e.key === 'ArrowLeft') prevVocab();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, filteredVocabList.length, checkAnswer, nextVocab, prevVocab]);

  // 未登录状态
  // 游客模式也允许使用（isLogin 始终为 true），无需登录拦截

  // 加载状态
  if (loading) return <div className="loading">加载中...</div>;

  // 错误状态
  if (error) return <div className="error">{error}</div>;

  // 无数据状态
  if (!filteredVocabList.length) {
    return <div className="no-data">该分类下无词汇</div>;
  }

  return (
    <div className="container">
      <div className="user-info">
        <span>欢迎，{currentUser.username}</span>
        <button onClick={handleLogout} className="logout-button">登出</button>
      </div>
      <h2>词汇练习</h2>
      
      {error && <div className="error-message">{error}</div>}
      {/* 添加手动加载分类按钮 */}
      <button onClick={manuallyFetchCategories} disabled={loading} style={{ marginBottom: '15px', padding: '8px 15px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
        {loading ? '加载中...' : '手动加载分类'}
      </button>
      
      {/* 分类选择 */}
      <div className="category-selector" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
        <label style={{ marginRight: '10px', fontWeight: 'bold' }}>选择分类：</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          disabled={loading || testMode}
          style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
        >
          <option value="all">全部词汇</option>
          {categories.length > 0 ? (
            categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))
          ) : (
            <option value="loading" disabled>加载分类中...</option>
          )}
        </select>
        <span className="count-info" style={{ marginLeft: '10px', color: '#666' }}>
          共 {filteredVocabList.length} 条
        </span>
        {categories.length === 0 && (
          <div style={{ color: 'red', marginTop: '5px', fontSize: '12px' }}>
            提示：未加载到分类数据，请检查网络或后端服务
          </div>
        )}
      </div>

      {/* 测试数量选择 */}
      {!testMode && (
        <div className="test-settings" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>选择测试数量：</label>
          <select
            value={testSize}
            onChange={(e) => setTestSize(parseInt(e.target.value))}
            disabled={loading || filteredVocabList.length === 0}
            style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '100px' }}
          >
            {availableTestSizes.map(size => (
              <option key={size} value={size}>{size} 个</option>
            ))}
          </select>
          <button
            onClick={startTest}
            disabled={loading || filteredVocabList.length === 0}
            style={{ marginLeft: '15px', padding: '8px 15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            开始测试
          </button>
        </div>
      )}

      {/* 测试进行中提示 */}
      {testMode && !testCompleted && (
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>测试进行中: {currentIndex + 1}/{testVocabList.length}</p>
          <button
            onClick={resetTest}
            style={{ marginTop: '5px', padding: '5px 10px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          >
            取消测试
          </button>
        </div>
      )}

      {/* 词汇展示 */}
      <div className="vocab-card">
        <p className="chinese">{testMode ? testVocabList[currentIndex].chinese : filteredVocabList[currentIndex].chinese}</p>
        <p className="index">
          {testMode ? `${currentIndex + 1}/${testVocabList.length}` : `${currentIndex + 1}/${filteredVocabList.length}`}
        </p>
        {(testMode ? testVocabList[currentIndex].categories : filteredVocabList[currentIndex].categories) && (
          <p className="category">
            分类：{(testMode ? testVocabList[currentIndex].categories : filteredVocabList[currentIndex].categories).map(c => c.name).join(', ')}
          </p>
        )}
      </div>

      {/* 输入区域 */}
      <div className="input-area">
        <input
          type="text"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder="请输入英文..."
          autoFocus
        />
        <button onClick={checkAnswer}>确认</button>
      </div>

      {/* 反馈 */}
      {feedback && (
        <div className={`feedback ${isCorrect ? 'correct' : 'wrong'}`}>
          {feedback}
        </div>
      )}

      {/* 导航按钮 */}
      <div className="nav-buttons">
        <button onClick={prevVocab}>上一个</button>
        <button onClick={nextVocab}>下一个</button>
      </div>

      {/* 测试结果区域 */}
      {testCompleted && (
        <div className="test-results" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f0f0f0', borderRadius: '8px', border: '2px solid #4CAF50' }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>测试完成！</h3>
          <div style={{ marginBottom: '15px' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold' }}>正确率: {Math.round((testResults.correct / Math.max(testResults.correct + testResults.incorrect, 1)) * 100)}%</p>
            <p>正确: {testResults.correct} / 错误: {testResults.incorrect}</p>
          </div>

          {testResults.wrongAnswers.length > 0 && (
            <div>
              <h4 style={{ color: '#d32f2f' }}>错误内容:</h4>
              <ul style={{ listStyleType: 'none', padding: 0 }}>
                {testResults.wrongAnswers.map((item, index) => (
                  <li key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', borderLeft: '4px solid #d32f2f' }}>
                    <p><strong>中文:</strong> {item.chinese}</p>
                    <p><strong>正确答案:</strong> {item.correctEnglish}</p>
                    <p><strong>你的答案:</strong> {item.userAnswer}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={resetTest}
              style={{ padding: '10px 15px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              返回练习
            </button>
            <button
              onClick={startTest}
              style={{ padding: '10px 15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              再次测试
            </button>
          </div>
        </div>
      )}

      {/* 复习建议区域 */}
      {!testMode && (
        <div className="review-section">
          <h4>复习建议</h4>
          {vocabStats.length === 0 ? (
            <p>暂无复习数据，请先进行测试</p>
          ) : (
            <div className="review-container">
              {/* 过滤和排序复习词汇 */}
              {(() => {
                // 基础过滤：复习日期<=今天且错误率>=设定阈值
                let filteredReviewVocabs = vocabStats.filter(stat => 
                  new Date(stat.next_review_date) <= new Date() && 
                  stat.error_rate >= reviewParams.minErrorRate
                );

                // 根据选中的分类进一步过滤
                if (selectedCategory && selectedCategory !== 'all') {
                  filteredReviewVocabs = filteredReviewVocabs.filter(stat => {
                    // 优先匹配 category_ids 数组（如果存在）
                    if (stat.category_ids && Array.isArray(stat.category_ids)) {
                      // 转换为字符串比较，避免类型不匹配
                      return stat.category_ids.some(catId => String(catId) === String(selectedCategory));
                    }
                    // 兼容旧字段：vocabulary_categories
                    else if (stat.vocabulary_categories && Array.isArray(stat.vocabulary_categories)) {
                      return stat.vocabulary_categories.some(catId => String(catId) === String(selectedCategory));
                    }
                    // 兼容旧字段：categories
                    else if (stat.categories && Array.isArray(stat.categories)) {
                      return stat.categories.some(catId => String(catId) === String(selectedCategory));
                    }
                    // 兼容旧字段：category_id 单个值
                    else if (stat.category_id) {
                      return String(stat.category_id) === String(selectedCategory);
                    }
                    return false;
                  });
                }

                // 优化排序：优先按难度降序，再按错误率降序
                filteredReviewVocabs.sort((a, b) => {
                  // 首先按难度降序
                  const difficultyDiff = b.difficulty - a.difficulty;
                  if (difficultyDiff !== 0) {
                    return difficultyDiff;
                  }
                  // 难度相同时，按错误率降序
                  return b.error_rate - a.error_rate;
                });

                // 取前10个
                const topReviewVocabs = filteredReviewVocabs.slice(0, 10);
                const hasReviewVocabs = topReviewVocabs.length > 0;

                return (
                  <>
                    <h5>需要复习的单词 ({filteredReviewVocabs.length})</h5>
                    
                    {/* 空状态提示 */}
                    {!hasReviewVocabs ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>
                        {selectedCategory && selectedCategory !== 'all' 
                          ? '该分类下暂无需要复习的词汇' 
                          : '暂无需要复习的词汇'}
                      </p>
                    ) : (
                      // 显示复习词汇列表
                      topReviewVocabs.map(stat => (
                        <div key={stat.id} className="review-item">
                          <span>{stat.vocabulary_chinese} ({stat.vocabulary_english}): </span>
                          <span className="review-info">
                            错误率: {stat.error_rate * 100}% | 下次复习: {stat.next_review_date} | 难度: {stat.difficulty}
                          </span>
                        </div>
                      ))
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* 参数设置区域 */}
      <div className="settings-section">
        <h4>复习参数设置</h4>
        <div className="settings-container">
          <div className="setting-item">
            <label>最小错误率阈值: </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={reviewParams.minErrorRate}
              onChange={(e) => setReviewParams(prev => ({...prev, minErrorRate: parseFloat(e.target.value)}))}
            />
            <span>{reviewParams.minErrorRate}</span>
          </div>
          <div className="setting-item">
            <label>最大复习间隔(天): </label>
            <input
              type="number"
              min="1"
              max="90"
              value={reviewParams.maxInterval}
              onChange={(e) => setReviewParams(prev => ({...prev, maxInterval: parseInt(e.target.value)}))}
            />
          </div>
          <div className="setting-item">
            <label>难度系数权重: </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={reviewParams.difficultyWeight}
              onChange={(e) => setReviewParams(prev => ({...prev, difficultyWeight: parseFloat(e.target.value)}))}
            />
            <span>{reviewParams.difficultyWeight}</span>
          </div>
        </div>
      </div>

      {/* 调试信息 */}
      <div className="debug">
        <h4>调试信息</h4>
        <pre>{debugInfo}</pre>
      </div>
    </div>
  );
};

export default VocabularyPractice;
    
